import {
  queryOptions,
  skipToken,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysUser } from '../queryKeys';
import { type UserSettingsAdmin } from '../types';

import { fetchUserSettingsAdmin } from './fetchUserSettingsAdmin';

/**
 * Generates the query options for the admin user settings query.
 *
 * @param userId - The ID of the user whose settings to fetch.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
// The options factory is the canonical entry point of this endpoint for prefetch and
// ensureQueryData, so it stays exported.
// fallow-ignore-next-line unused-export
export const getUserSettingsAdminQueryOptions = <TData = UserSettingsAdmin>({
  userId,
  options,
}: {
  userId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<UserSettingsAdmin, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: userId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchUserSettingsAdmin(userId, { signal })
      : skipToken,
    queryKey: queryKeysUser.getSettingsAdmin(userId),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query based custom hook for getting the settings stored on a user.
 * Requires VESTIGAS admin access.
 *
 * @param userId - The ID of the user whose settings to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/get_settings_for_user_vadmin_user__user_id__settings_get
 */
export const useQueryUserSettingsAdmin = <TData = UserSettingsAdmin>(
  userId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<UserSettingsAdmin, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getUserSettingsAdminQueryOptions<TData>({ userId, options }));
